import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { fetchTeam } from "./api";
import type { TeamMember } from "./types";

interface RosterValue {
  /** Everyone who can own a request or lead an account, with their role and
   *  weekly capacity. Empty until loaded. */
  members: TeamMember[];
  reload: () => void;
}

const RosterContext = createContext<RosterValue | null>(null);

/**
 * Loads the team roster once and shares it with every owner/CE-lead dropdown,
 * so the choices come from the team_members table rather than a hardcoded
 * constant. Sits inside AuthProvider (it needs an authenticated session).
 */
export function RosterProvider({ children }: { children: ReactNode }) {
  const [members, setMembers] = useState<TeamMember[]>([]);

  const load = () => {
    void fetchTeam()
      .then(setMembers)
      .catch(() => setMembers([]));
  };

  useEffect(load, []);

  return <RosterContext.Provider value={{ members, reload: load }}>{children}</RosterContext.Provider>;
}

export function useRoster(): RosterValue {
  const ctx = useContext(RosterContext);
  if (!ctx) throw new Error("useRoster must be used inside <RosterProvider>");
  return ctx;
}
